import { type PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ANNOUNCEMENT_REPORT_PAGE_URL } from '@/api/core/links';
import { AnnouncementEndpoints } from '@/api/endpoints/AnnouncementEndpoints';
import { type AnnouncementDto } from '@/app/models/AnnouncementDto';
import { type DataTableColumnDef } from '@/app/models/DataTableColumnDef';
import { DataTable } from '@/app/modules/table/DataTable';
import DataTableBody from '@/app/modules/table/components/DataTableBody';
import DataTableControls from '@/app/modules/table/components/DataTableControls';
import DataTablePagination from '@/app/modules/table/components/DataTablePagination';
import AddButton from '@/app/modules/widgets/Buttons/AddButton';
import AnnouncementListFilter from '@/app/pages/notifications/announcements/components/AnnouncementListFilter';
import { managedAnnouncementColumns } from '@/app/pages/notifications/announcements/core/_columns';
import {
  ANNOUNCEMENT_STATUS_OPTIONS,
  MANAGED_ANNOUNCEMENTS_TABLE_KEY,
} from '@/app/pages/notifications/announcements/core/_consts';
import { useAnnouncementAction } from '@/app/pages/notifications/announcements/core/_hooks';
import {
  AnnouncementConfirmAction,
  type ConfirmActionConfig,
  ConfirmActionVariant,
  type ManagedAnnouncementHandlers,
  type PendingConfirmation,
} from '@/app/pages/notifications/announcements/core/_models';
import { close, publish, remove } from '@/app/pages/notifications/announcements/core/_requests';
import AnnouncementValidityModal from '@/app/pages/notifications/announcements/modal/AnnouncementValidityModal';
import ConfirmActionModal from '@/app/pages/notifications/announcements/modal/ConfirmActionModal';
import CreateAnnouncementModal from '@/app/pages/notifications/announcements/modal/CreateAnnouncementModal';
import EditAnnouncementModal from '@/app/pages/notifications/announcements/modal/EditAnnouncementModal';
import ViewAnnouncementModal from '@/app/pages/notifications/announcements/modal/ViewAnnouncementModal';

const CONFIRM_ACTIONS: Record<AnnouncementConfirmAction, ConfirmActionConfig> = {
  [AnnouncementConfirmAction.PUBLISH]: {
    title: 'Publicar comunicado',
    question: (announcementTitle) => `Publicar "${announcementTitle}"? Depois de publicado, o texto não muda mais.`,
    confirmLabel: 'Publicar',
    variant: ConfirmActionVariant.PRIMARY,
    request: publish,
    successMessage: 'Comunicado publicado',
  },
  [AnnouncementConfirmAction.CLOSE]: {
    title: 'Encerrar comunicado',
    question: (announcementTitle) => `Encerrar "${announcementTitle}"? Ele deixa de aparecer para quem ainda não leu.`,
    confirmLabel: 'Encerrar',
    variant: ConfirmActionVariant.DANGER,
    request: close,
    successMessage: 'Comunicado encerrado',
  },
  [AnnouncementConfirmAction.REMOVE]: {
    title: 'Excluir rascunho',
    question: (announcementTitle) => `Excluir o rascunho "${announcementTitle}"?`,
    confirmLabel: 'Excluir',
    variant: ConfirmActionVariant.DANGER,
    request: remove,
    successMessage: 'Rascunho excluído',
  },
};

const ManagedAnnouncementsTab = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [duplicateFromId, setDuplicateFromId] = useState<string>();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingId, setEditingId] = useState<string>();
  const [viewingId, setViewingId] = useState<string>();
  const [validityTarget, setValidityTarget] = useState<AnnouncementDto>();
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation>();

  const confirmAction = useAnnouncementAction();

  // Origem e id ficam até a próxima abertura: limpá-los ao fechar trocaria título e campos durante o fade-out.
  const openCreateModal = useCallback((sourceId?: string) => {
    setDuplicateFromId(sourceId);
    setIsCreateOpen(true);
  }, []);

  const openEditModal = useCallback((announcement: AnnouncementDto) => {
    setEditingId(announcement.id);
    setIsEditOpen(true);
  }, []);

  const runConfirmedAction = () => {
    if (!pendingConfirmation) return;
    const { action, announcement } = pendingConfirmation;
    const { request, successMessage } = CONFIRM_ACTIONS[action];
    confirmAction.mutate(
      { id: announcement.id, request, successMessage },
      { onSuccess: () => setPendingConfirmation(undefined) },
    );
  };

  const confirmation = pendingConfirmation ? CONFIRM_ACTIONS[pendingConfirmation.action] : undefined;
  const confirmationQuestion =
    pendingConfirmation && confirmation ? confirmation.question(pendingConfirmation.announcement.title) : '';

  const columns = useMemo(() => {
    const handlers: ManagedAnnouncementHandlers = {
      onView: (announcement) => setViewingId(announcement.id),
      onEdit: openEditModal,
      onPublish: (announcement) => setPendingConfirmation({ action: AnnouncementConfirmAction.PUBLISH, announcement }),
      onDuplicate: (announcement) => openCreateModal(announcement.id),
      onRemove: (announcement) => setPendingConfirmation({ action: AnnouncementConfirmAction.REMOVE, announcement }),
      onReport: (announcementId) => {
        const reportUrl = ANNOUNCEMENT_REPORT_PAGE_URL(announcementId);
        navigate(reportUrl);
      },
      onValidity: setValidityTarget,
      onClose: (announcement) => setPendingConfirmation({ action: AnnouncementConfirmAction.CLOSE, announcement }),
    };
    const managedColumns = managedAnnouncementColumns(handlers);
    return managedColumns as DataTableColumnDef<object>[];
  }, [navigate, openCreateModal, openEditModal]);

  return (
    <>
      <CreateAnnouncementModal
        duplicateFromId={duplicateFromId}
        show={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />
      <EditAnnouncementModal announcementId={editingId} show={isEditOpen} onClose={() => setIsEditOpen(false)} />
      {viewingId ? (
        <ViewAnnouncementModal key={viewingId} announcementId={viewingId} onClose={() => setViewingId(undefined)} />
      ) : null}
      {validityTarget ? (
        <AnnouncementValidityModal
          key={validityTarget.id}
          announcementId={validityTarget.id}
          endDate={validityTarget.end_date}
          startDate={validityTarget.start_date}
          onClose={() => setValidityTarget(undefined)}
        />
      ) : null}
      {confirmation ? (
        <ConfirmActionModal
          confirmLabel={confirmation.confirmLabel}
          isPending={confirmAction.isPending}
          question={confirmationQuestion}
          title={confirmation.title}
          variant={confirmation.variant}
          onClose={() => setPendingConfirmation(undefined)}
          onConfirm={runConfirmedAction}
        />
      ) : null}
      <DataTable
        actionComponent={<AddButton label="Novo comunicado" onAdd={() => openCreateModal()} />}
        columns={columns}
        direction="desc"
        endpoint={AnnouncementEndpoints.list}
        filterComponents={[<AnnouncementListFilter key="filters" statusOptions={ANNOUNCEMENT_STATUS_OPTIONS} />]}
        isRefreshable={true}
        searchPlaceHolder="Pesquise pelo título"
        sort="registration_date"
        tableKey={MANAGED_ANNOUNCEMENTS_TABLE_KEY}
        variant="list"
        onRowClick={(row) => setViewingId((row.original as AnnouncementDto).id)}
      >
        <DataTableControls />
        {children}
        <DataTableBody showHeader={true} />
        <DataTablePagination />
      </DataTable>
    </>
  );
};

export default ManagedAnnouncementsTab;
